import { useState } from "react";
import {
    FileText,
    Users,
    Briefcase,
    Search,
    GraduationCap,
    CheckCircle,
    ArrowRight,
    Cpu,
    Cloud,
    Shield,
    Server,
    Brain,
    Database,
} from "lucide-react";

export default function Services() {
    const [activeCategory, setActiveCategory] = useState("career");

    const careerServices = [
        {
            title: "Resume Optimization",
            description:
                "We don't just fix typos; we rebuild your resume from the ground up to pass ATS and catch recruiter eyes.",
            icon: <FileText className="text-brand-primary" size={32} />,
            features: [
                "Keyword Research",
                "Modern Templates",
                "LinkedIn Makeover",
            ],
            stats: { label1: "Resumes Built", value1: "8,400+", label2: "ATS Pass Rate", value2: "98%", value3: "95" },
        },
        {
            title: "Interview Coaching",
            description:
                "Practice with industry veterans. We provide role-specific mock interviews and feedback.",
            icon: <Users className="text-brand-dark" size={32} />,
            features: [
                "Mock Sessions",
                "Body Language Tips",
                "Salary Negotiation",
            ],
            stats: { label1: "Mock Sessions", value1: "3,200+", label2: "Offer Rate", value2: "87%", value3: "95" },
        },
        {
            title: "Direct Job Placement",
            description:
                "Get priority access to our partner network and direct hiring pipelines.",
            icon: <Briefcase className="text-brand-accent" size={32} />,
            features: [
                "Exclusive Roles",
                "Internal Referrals",
                "End-to-end Support",
            ],
            stats: { label1: "Jobs Placed", value1: "1,500+", label2: "Partner Firms", value2: "340+", value3: "95" },
        },
    ];

    const itServices = [
        {
            title: "Software & App Development",
            description:
                "Full-cycle development of custom enterprise software, mobile apps, and robust web systems tailored for performance and scale.",
            icon: <Cpu className="text-brand-primary" size={32} />,
            features: [
                "Web & Mobile Portals",
                "API Integrations",
                "React / Node / Python",
            ],
            stats: { label1: "Apps Completed", value1: "120+", label2: "Client Rating", value2: "4.9/5", value3: "98" },
        },
        {
            title: "Cloud & DevOps Solutions",
            description:
                "Architect and manage reliable cloud architectures with secure environments, high availability, and automated workflows.",
            icon: <Cloud className="text-brand-dark" size={32} />,
            features: [
                "AWS / Azure Migrations",
                "Kubernetes & Docker",
                "CI/CD Orchestration",
            ],
            stats: { label1: "Cloud Uptime", value1: "99.99%", label2: "Cost Optimized", value2: "30%", value3: "99" },
        },
        {
            title: "Cybersecurity & Compliance",
            description:
                "Keep your assets safe with complete vulnerability analysis, penetration tests, system monitoring, and regulatory compliance.",
            icon: <Shield className="text-brand-accent" size={32} />,
            features: [
                "Vulnerability Checks",
                "Encrypted Connections",
                "SOC2 / GDPR Prep",
            ],
            stats: { label1: "Audits Completed", value1: "450+", label2: "Threats Blocked", value2: "100%", value3: "96" },
        },
        {
            title: "Managed Support & Consulting",
            description:
                "Proactive monitoring and management of your network hardware, database engines, and overall tech strategy.",
            icon: <Server className="text-brand-primary" size={32} />,
            features: [
                "24/7 Operations Desk",
                "Database Tuning",
                "Systems Engineering",
            ],
            stats: { label1: "Response Time", value1: "<15 mins", label2: "Incidents Solved", value2: "99.4%", value3: "97" },
        },
        {
            title: "AI & Machine Learning",
            description:
                "Incorporate cognitive features into your operations, from smart chatbots and automated text processors to advanced analytics models.",
            icon: <Brain className="text-brand-dark" size={32} />,
            features: [
                "Large Language Models",
                "Automated Analytics",
                "Intelligent Chatbots",
            ],
            stats: { label1: "Models Trained", value1: "80+", label2: "Efficiency Gain", value2: "45%", value3: "95" },
        },
        {
            title: "Data Engineering & APIs",
            description:
                "Design fast, safe database architectures, ETL processing nodes, and standard rest/graphql APIs to power cross-system syncs.",
            icon: <Database className="text-brand-accent" size={32} />,
            features: [
                "Database Tuning",
                "ETL Pipelines",
                "GraphQL/REST APIs",
            ],
            stats: { label1: "Queries Optimized", value1: "2M+/day", label2: "API Response", value2: "<80ms", value3: "98" },
        },
    ];

    // ✅ Smooth scroll with navbar offset
    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        const offset = 80;

        if (element) {
            const top = element.offsetTop - offset;
            window.scrollTo({
                top,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div className="relative min-h-screen bg-brand-light text-slate-900 overflow-hidden">
            {/* BACKGROUND */}
            <div className="absolute inset-0 bg-brand-radial -z-10"></div>

            {/* HERO */}
            <section className="pt-32 pb-20 px-6">
                <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight">
                            Comprehensive Tools for{" "}
                            <span className="block sm:inline bg-brand-gradient bg-clip-text text-transparent">
                                Career Transformation
                            </span>
                        </h1>

                        <p className="text-xl text-slate-600 mb-8">
                            From your first resume draft to final salary negotiation, we cover
                            everything.
                        </p>

                        <div className="flex gap-4">
                            <button onClick={() => scrollToSection('contact')} className="bg-brand-dark hover:bg-brand-primary text-white px-8 py-4 rounded-xl font-bold transition-all">
                                View Pricing
                            </button>

                            <button onClick={() => scrollToSection('contact')} className="border border-brand-primary/30 hover:bg-brand-primary/10 px-8 py-4 rounded-xl font-bold transition-all">
                                Free Consultation
                            </button>
                        </div>
                    </div>

                    {/* HERO UI */}
                    <div className="hidden md:block">
                        <div className="bg-white/70 backdrop-blur-xl p-8 rounded-3xl border border-white/40 shadow-2xl">
                            <div className="space-y-4">
                                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                <div className="h-4 bg-gray-200 rounded w-1/2"></div>

                                <div className="h-20 bg-brand-primary/10 rounded-xl border border-brand-primary/20 flex items-center px-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center">
                                            <CheckCircle size={20} className="text-white" />
                                        </div>
                                        <span className="text-brand-primary font-medium">
                                            ATS Score: 98% Optimized
                                        </span>
                                    </div>
                                </div>

                                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* PROCESS */}
            <section className="py-24 px-6 max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-3xl font-bold mb-4">
                        Our Success Process
                    </h2>
                    <p className="text-slate-600">From applicant → employee</p>
                </div>

                <div className="grid md:grid-cols-4 gap-8">
                    {[
                        { step: "01", name: "Assessment", icon: <Search size={20} /> },
                        { step: "02", name: "Refining", icon: <FileText size={20} /> },
                        { step: "03", name: "Training", icon: <GraduationCap size={20} /> },
                        { step: "04", name: "Placement", icon: <Briefcase size={20} /> },
                    ].map((item, i) => (
                        <div key={i} className="relative text-center">
                            <div className="w-16 h-16 bg-brand-light text-brand-primary rounded-full flex items-center justify-center mx-auto mb-4 border border-brand-primary/20">
                                {item.icon}
                            </div>

                            <h4 className="font-bold text-lg">{item.name}</h4>

                            <span className="text-xs font-bold text-brand-primary/40 absolute -top-2 left-1/2 translate-x-4">
                                {item.step}
                            </span>

                            {i < 3 && (
                                <div className="hidden lg:block absolute top-8 left-[65%] w-full h-[2px] bg-brand-primary/10"></div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* SERVICES */}
            <section className="py-24 px-6 bg-white/20">
                <div className="max-w-7xl mx-auto">
                    {/* Tabs switcher */}
                    <div className="flex justify-center mb-20">
                        <div className="bg-white/60 backdrop-blur-md p-1.5 rounded-2xl border border-slate-200/50 inline-flex gap-2 shadow-sm">
                            <button
                                onClick={() => setActiveCategory("career")}
                                className={`px-6 py-3 rounded-xl font-bold text-sm transition-all duration-300 ${
                                    activeCategory === "career"
                                        ? "bg-brand-dark text-white shadow-md"
                                        : "text-slate-600 hover:text-slate-900"
                                }`}
                            >
                                Career Bridging
                            </button>
                            <button
                                onClick={() => setActiveCategory("it")}
                                className={`px-6 py-3 rounded-xl font-bold text-sm transition-all duration-300 ${
                                    activeCategory === "it"
                                        ? "bg-brand-dark text-white shadow-md"
                                        : "text-slate-600 hover:text-slate-900"
                                }`}
                            >
                                Enterprise IT Services
                            </button>
                        </div>
                    </div>

                    <div className="space-y-20">
                        {(activeCategory === "career" ? careerServices : itServices).map((service, index) => (
                            <div
                                key={index}
                                className={`flex flex-col md:flex-row gap-12 items-center ${index % 2 !== 0 ? "md:flex-row-reverse" : ""
                                    }`}
                            >
                                {/* LEFT */}
                                <div className="flex-1">
                                    <div className="mb-6">{service.icon}</div>

                                    <h2 className="text-4xl font-bold mb-4">
                                        {service.title}
                                    </h2>

                                    <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                                        {service.description}
                                    </p>

                                    <ul className="grid grid-cols-2 gap-4 mb-8">
                                        {service.features.map((feature, i) => (
                                            <li
                                                key={i}
                                                className="flex items-center gap-2 text-slate-700 font-medium"
                                            >
                                                <CheckCircle
                                                    size={18}
                                                    className="text-brand-primary"
                                                />
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>

                                    <button onClick={() => scrollToSection('contact')} className="flex items-center gap-2 font-bold text-brand-primary hover:gap-4 transition-all">
                                        Learn more <ArrowRight size={20} />
                                    </button>
                                </div>

                                {/* RIGHT - VISUAL UI */}
                                <div className="flex-1 w-full">
                                    <div className="aspect-video rounded-[2rem] bg-white shadow-2xl border border-slate-200 p-6 flex flex-col justify-between hover:scale-105 transition duration-300">

                                        {/* Header */}
                                        <div className="flex items-center justify-between">
                                            <div className="text-sm font-semibold text-slate-700">
                                                {activeCategory === "career" ? "Candidate Dashboard" : "Enterprise Console"}
                                            </div>
                                            <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full">
                                                Active
                                            </span>
                                        </div>

                                        {/* Progress */}
                                        <div className="mt-4">
                                            <div className="flex justify-between text-xs text-slate-500 mb-1">
                                                <span>{activeCategory === "career" ? "Profile Strength" : "Infrastructure Health"}</span>
                                                <span>{service.stats.value3}%</span>
                                            </div>
                                            <div className="w-full h-2 bg-slate-200 rounded-full">
                                                <div
                                                    style={{ width: `${service.stats.value3}%` }}
                                                    className="h-2 bg-brand-primary rounded-full"
                                                ></div>
                                            </div>
                                        </div>

                                        {/* Stats */}
                                        <div className="grid grid-cols-2 gap-4 mt-6">
                                            <div className="bg-brand-primary/10 p-4 rounded-xl">
                                                <p className="text-xs text-slate-500">{service.stats.label1}</p>
                                                <h3 className="text-xl font-bold text-brand-primary">
                                                    {service.stats.value1}
                                                </h3>
                                            </div>

                                            <div className="bg-brand-accent/10 p-4 rounded-xl">
                                                <p className="text-xs text-slate-500">{service.stats.label2}</p>
                                                <h3 className="text-xl font-bold text-brand-accent">
                                                    {service.stats.value2}
                                                </h3>
                                            </div>
                                        </div>

                                        {/* Footer */}
                                        <div className="mt-6 flex items-center justify-between">
                                            <span className="text-xs text-slate-500">
                                                Last updated: 2h ago
                                            </span>

                                            <button onClick={() => scrollToSection('contact')} className="text-xs font-semibold text-white bg-brand-dark px-3 py-2 rounded-lg hover:bg-brand-primary transition">
                                                View Details
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 px-6 text-center">
                <div className="max-w-3xl mx-auto">
                    <h2 className="text-4xl font-bold mb-6">
                        Unsure which plan fits you?
                    </h2>

                    <p className="text-lg text-slate-600 mb-10">
                        Speak with a career counselor for a free roadmap session.
                    </p>

                    <button onClick={() => scrollToSection('contact')} className="bg-brand-gradient text-white px-12 py-5 rounded-2xl font-bold text-lg shadow-xl hover:opacity-90 transition-all">
                        Schedule Free Call
                    </button>
                </div>
            </section>
        </div>
    );
}